// Pure timing logic for AuthContext's live-permission refresh (RBAC Slice 3,
// goal 2 — "apply permission changes without requiring re-login").
//
// `user.permissions` (the effective-permissions array every nav/route gate
// reads via hasAnyPermission/hasPermission) is set once at login and again
// whenever refreshUser() runs — but nothing used to call refreshUser() on
// its own. If an admin granted or revoked a permission for a user mid-
// session, that user's sidebar/header stayed on the stale snapshot until
// they happened to hit a page that calls refreshUser() (AccountPage, after
// a save) or logged out and back in. AuthContext now re-fetches /me:
//   1. on a background interval (REFRESH_INTERVAL_MS) — catches a change
//      even in a tab that's never blurred/refocused, and
//   2. when the tab regains focus/visibility — the common case (an admin
//      edits a role in one tab, the user switches back to another),
//      throttled by shouldRefreshOnFocus so tab-flicking can't hammer
//      /api/auth/me.
// This module is the pure "when do we refetch" decision, kept out of the
// React effect so it's covered by `node --test` (npm run test:logic, the
// suite CI actually runs) instead of only exercised — or not — inside a
// component effect.

export const REFRESH_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export const MIN_FOCUS_REFRESH_INTERVAL_MS = 30 * 1000 // 30 seconds

// `lastRefreshedAt`/`now` are epoch-ms numbers (or null/undefined for
// "never refreshed yet", which always refreshes). Any non-finite input is
// treated as "unknown" and refreshes, rather than throwing or silently
// skipping a refresh the caller expected.
export function shouldRefreshOnFocus(lastRefreshedAt, now) {
  if (typeof lastRefreshedAt !== 'number' || !Number.isFinite(lastRefreshedAt)) return true
  if (typeof now !== 'number' || !Number.isFinite(now)) return true
  return now - lastRefreshedAt >= MIN_FOCUS_REFRESH_INTERVAL_MS
}
